<template>
  <div class="potential-member" :class="itemTypeRef.classes">
    <div v-if="!hideStatus" class="status-icon"></div>
    <input type="checkbox" v-if="sharingMode" class="form-check-input" v-model="selectedRef" :disabled="!canSelectRef"/>
    <div class="member-area">
      <button type="button" v-if="!disabled" class="overlay" @click="emit('click')"></button>
      <img v-if="isProfileSharedRef" src="@/assets/icons/person_gray_150.png" class="member-icon"/>
      <img v-else src="@/assets/icons/person_dotted_80.png" class="member-icon"/>
      <div class="member-name">
        {{ displayNameRef }}
        <span v-if="pm.profilePerson?.displayProperties.assumeDeceased" class="deceased">(deceased)</span>
      </div>
    </div>
    <button type="button" v-if="showGroupInviteActionRef" class="btn btn-inline btn-link member-action" @click="emit('invite')">Invite</button>
  </div>
</template>

<style lang="scss" scoped>
.potential-member {
  position: relative;
  display: flex;
  align-items: center;
}

input[type="checkbox"] {
  margin-top: -1px;
  margin-right: 0.5rem;
}

.member-icon {
  width: 18px;
  opacity: 60%;
}

.member-name {
  .deceased {
    font-size: 0.875rem;
    color: #aaa;
  }
}

.member-action {
  margin-left: 0.5rem;
  font-size: 0.875rem;
}


.status-icon {
  position: absolute;
  width: 1.2rem;
  height: 1.2rem;
  top: 3px;
  left: -1.5rem;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
}

.invite-status {
  margin-bottom: 0.5rem;
  grid-row: 2;
  grid-column: 2;
  line-height: 1.2;
  font-size: 0.875rem;
  color: #888;

  button {
    margin: 0 0.25rem;
    display: inline-block;
    line-height: 1.2;
  }
}

.member-area {
  position: relative;
  display: grid;
  grid-template-columns: 24px max-content;
  align-items: center;
  color: #333;
}

.potential-member {
  &.owner .status-icon {
    background-image: url('@/assets/icons/crown.svg');
    filter: contrast(0.5) sepia(1) hue-rotate(5deg) saturate(3);
  }

  &.sharing-invite, &.group-invite {
    .status-icon {
      background-image: url('@/assets/icons/mail_gray_60.png');
      filter: brightness(0) saturate(100%) invert(63%) sepia(76%) saturate(5097%) hue-rotate(184deg) brightness(100%) contrast(94%);
    }
  }

  // &.recruiter .status-icon {
  //   background-image: url('@/assets/icons/invite_gray_150.png');
  //   filter: brightness(0) saturate(100%) invert(51%) sepia(90%) saturate(343%) hue-rotate(56deg) brightness(91%) contrast(86%);
  // }

  &.profile {
    .member-icon {
      opacity: 30%;
    }
  }

  &.suggested {
    .member-icon {
      opacity: 80%;
    }

    .member-name {
      color: #aaa;
    }
  }
}
</style>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { PotentialMember } from '@/manage/familygroups/FamilyGroupMemberManager'
import { GroupMemberRole } from '@/gp/GroupAdminModel'
import { ProfileSharingState } from './ProfileSharingState'

const props = defineProps<{
  pm: Readonly<PotentialMember>
  allowAdd?: boolean
  sharingMode?: boolean
  hideStatus?: boolean
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'click'): void,
  (e: 'invite'): void,
}>()

const profileSharingState = inject<ProfileSharingState>('profileSharingState')!

const selectedRef = computed({
  get: () => profileSharingState.isShared(props.pm.profileGroupId),
  set: value => profileSharingState.shareProfiles([props.pm.profileGroupId!], value)
})

const memIsOwnerRef = computed(() => props.pm.member?.role == GroupMemberRole.Owner)
const isProfileSharedRef = computed(() => profileSharingState.isShared(props.pm.profileGroupId!))
const canSelectRef = computed(() => props.pm.canModifyProfile && !props.pm.hasPendingGroupInvite)
const canInviteOthersRef = computed(() => props.pm.member?.canInviteOthers)
const showGroupInviteActionRef = computed(() => props.allowAdd && isProfileSharedRef.value && props.pm.canBeInvited)
const showSharingInviteRef = computed(() => !isProfileSharedRef.value && props.pm.lastSharingInvite?.isPending)

const itemTypeRef = computed(() => {
  const classes: string[] = []
  let text = ''
  if (memIsOwnerRef.value && !props.hideStatus) {
    classes.push('owner')
    text = 'Owner'
  }
  else if (canInviteOthersRef.value) {
    classes.push('recruiter')
    text = 'Member'
  }
  else if (props.pm.member) {
    classes.push('member')
    text = 'Member'
  }
  else if (isProfileSharedRef.value) {
    classes.push('profile')
    text = 'Shared profile'
  }
  else {
    classes.push('suggested')
    text = 'Suggested member'
  }

  if (showSharingInviteRef.value) {
    classes.push('sharing-invite')
    text = 'Share requested'
  }
  if (props.pm.hasPendingGroupInvite) {
    classes.push('group-invite')
    text = 'Invited'
  }

  return { classes, text }
})

const displayNameRef = computed(() => props.pm.displayName ?? '(no name)')

</script>
